import { useCallback } from "react";
import { useNavigate, useOutletContext } from "react-router";

import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { VcsProvider } from "types/generated";
import { SpacesContext } from "views/Account/SpacesProvider";

import { useLayoutActions } from "../../hooks/useLayoutActions";
import { useLayoutHeading } from "../../hooks/useLayoutHeading";
import { IntegrationsOutletContext } from "../../types";
import VCSIntegrationsList from "../shared/VcsList";
import { GITHUB_INTEGRATION_DETAILS_DRAWER_ID } from "../shared/VcsList/constants";
import SetupButton from "../shared/VcsList/SetupButton";
import useSearchVcsIntegrations from "../shared/VcsList/useSearchVCSIntegrations";
import { getEditIntegrationURL } from "../shared/VcsShared/helpers";
import { showVCSIntegrationsBuiltInGitHubDetailsDrawer } from "./components/BuiltInGitHubDetailsDrawer";
import VCSIntegrationsBuiltInGitHubListItem from "./components/BuiltInGitHubListItem";
import useGetDefaultIntegration from "./useGetDefaultIntegration";

const Github = () => {
  const providers = [VcsProvider.Github, VcsProvider.GithubEnterprise];
  const navigate = useNavigate();
  const { hasEntityCreateAccess } = useTypedContext(SpacesContext);
  const { integration } = useOutletContext<IntegrationsOutletContext>();

  useTitle(integration.name);
  useLayoutHeading(integration.name);

  useLayoutActions(
    hasEntityCreateAccess ? <SetupButton provider={VcsProvider.Github} /> : undefined
  );

  const VcsListProps = useSearchVcsIntegrations(providers);
  const { builtInGithubIntegration, loading: builtInIntegrationLoading } =
    useGetDefaultIntegration();

  const handleBuiltInGitHubIntegrationEdit = useCallback(() => {
    navigate(getEditIntegrationURL(VcsProvider.Github, "builtin"));
  }, [navigate]);

  const handleBuiltInGitHubOpenDetailsDrawer = useCallback(() => {
    if (!builtInGithubIntegration) {
      return;
    }

    showVCSIntegrationsBuiltInGitHubDetailsDrawer({
      id: GITHUB_INTEGRATION_DETAILS_DRAWER_ID,
      details: builtInGithubIntegration,
    });
  }, [builtInGithubIntegration]);

  return (
    <VCSIntegrationsList
      {...VcsListProps}
      isPageLoading={VcsListProps.isPageLoading || builtInIntegrationLoading}
      builtInIntegration={
        builtInGithubIntegration ? (
          <VCSIntegrationsBuiltInGitHubListItem
            onEdit={handleBuiltInGitHubIntegrationEdit}
            onShowDetails={handleBuiltInGitHubOpenDetailsDrawer}
          />
        ) : null
      }
      provider={VcsProvider.Github}
    />
  );
};

export default Github;
